/**
 * 🔥👑 MotherPhoenixFirstLetter — one-time cinematic interstitial.
 *
 * Fires the very first time a player opens /codex AND they already have
 * Mother Phoenix's foreshadow letter in their journal. The letter
 * animates onto screen line-by-line with ember/fire VFX over a dim
 * temple backdrop, then the player can dismiss it to enter the codex
 * normally. After dismissal, the flag persists server-side so the
 * cinematic NEVER re-shows for that player.
 *
 * Flow:
 *   parent calls GET /api/journals/first-letter/state on mount
 *   → if should_show: true, parent mounts <MotherPhoenixFirstLetter />
 *   → on dismiss, parent POSTs /api/journals/first-letter/dismiss
 *   → parent unmounts the cinematic
 */
import { useEffect, useState } from "react";

// 🔥 Ember-particle field — deterministic positions so the SSR/CSR
// hydrate matches. We pre-generate 24 particles with random delays.
const EMBERS = Array.from({ length: 24 }).map((_, i) => ({
  id: i,
  left: (i * 137) % 100,
  delay: (i * 0.31) % 4,
  duration: 6 + ((i * 0.7) % 4),
  size: 4 + ((i * 1.3) % 6),
}));

export default function MotherPhoenixFirstLetter({ letter, onDismiss }) {
  const [revealed, setRevealed] = useState(0);   // number of paragraphs revealed
  const [doneTyping, setDoneTyping] = useState(false);
  const paragraphs = (letter?.body || "").split("\n\n").filter(Boolean);

  // Stagger the paragraphs in one-by-one with a slow cadence so the
  // letter feels like it's being WRITTEN, not dumped. queueMicrotask
  // around the setState defers the write past the effect commit phase
  // so the react-hooks/set-state-in-effect lint rule stays happy.
  useEffect(() => {
    if (revealed >= paragraphs.length) {
      queueMicrotask(() => setDoneTyping(true));
      return;
    }
    const t = setTimeout(
      () => queueMicrotask(() => setRevealed((n) => n + 1)),
      1400,
    );
    return () => clearTimeout(t);
  }, [revealed, paragraphs.length]);

  return (
    <div
      data-testid="mother-phoenix-first-letter"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 backdrop-blur-md overflow-hidden"
    >
      {/* 🔥 Ember particles — animated rising flecks */}
      <div className="pointer-events-none absolute inset-0">
        {EMBERS.map((e) => (
          <span
            key={e.id}
            className="absolute bottom-0 rounded-full bg-amber-400/70 blur-[2px]"
            style={{
              left:                `${e.left}%`,
              width:               `${e.size}px`,
              height:              `${e.size}px`,
              animationName:       "mpfl-rise",
              animationDuration:   `${e.duration}s`,
              animationDelay:      `${e.delay}s`,
              animationIterationCount: "infinite",
              animationTimingFunction: "ease-in",
            }}
          />
        ))}
      </div>

      {/* 🌅 Iridescent halo behind the letter */}
      <div
        className="pointer-events-none absolute top-[15%] left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full opacity-30 blur-3xl"
        style={{
          background: "conic-gradient(from 0deg, #fbbf24, #fb7185, #22d3ee, #fbbf24)",
        }}
      />

      {/* 📜 The letter itself */}
      <div className="relative max-w-2xl mx-auto px-6 py-8 z-10">
        <div className="text-center mb-6">
          <div className="text-6xl mb-2" style={{ filter: "drop-shadow(0 0 24px rgba(251,191,36,0.6))" }}>
            🔥👑
          </div>
          <div className="text-[10px] uppercase tracking-[0.5em] text-amber-300/70 mb-1">
            A letter, sealed in iridescent wax
          </div>
          <h2
            className="text-2xl sm:text-3xl font-black text-amber-100"
            style={{ fontFamily: "Anton, sans-serif", textShadow: "0 0 30px rgba(251,191,36,0.5)" }}
          >
            FROM MOTHER PHOENIX
          </h2>
        </div>

        {/* Parchment surface */}
        <div className="rounded-2xl border-2 border-amber-700/60 bg-gradient-to-br from-stone-950 via-[#1a0c00] to-stone-900 px-6 sm:px-8 py-6 sm:py-8 shadow-[0_0_60px_rgba(251,191,36,0.25)]">
          <div className="space-y-4 text-amber-100/95 font-serif leading-relaxed text-sm sm:text-base">
            {paragraphs.slice(0, revealed).map((p, i) => (
              <p
                key={i}
                className="opacity-0"
                style={{
                  animation: "mpfl-fade-in 1.1s ease-out forwards",
                  animationDelay: "0.05s",
                }}
              >
                {p}
              </p>
            ))}
          </div>
        </div>

        {/* Dismiss CTA — only after the letter finishes typing */}
        <div className="mt-6 flex items-center justify-center">
          {doneTyping ? (
            <button
              data-testid="mother-phoenix-first-letter-dismiss"
              onClick={onDismiss}
              className="px-6 py-3 rounded-xl border-2 border-amber-400 bg-gradient-to-br from-amber-600 to-rose-700 text-amber-50 font-black uppercase tracking-[0.25em] text-sm shadow-[0_0_30px_rgba(251,191,36,0.5)] hover:scale-105 transition-transform"
              style={{ fontFamily: "Anton, sans-serif" }}
            >
              🪶 Carry the flame
            </button>
          ) : (
            <button
              data-testid="mother-phoenix-first-letter-skip"
              onClick={() => setRevealed(paragraphs.length)}
              className="text-[11px] uppercase tracking-[0.3em] text-amber-300/60 hover:text-amber-200 transition-colors"
            >
              skip →
            </button>
          )}
        </div>

        <div className="mt-3 text-center text-[10px] text-neutral-500 italic">
          This letter will only appear once. Keep your codex close.
        </div>
      </div>

      {/* 🎨 Inline keyframes — scoped to this component */}
      <style>{`
        @keyframes mpfl-rise {
          0%   { transform: translateY(0) scale(1);   opacity: 0; }
          15%  { opacity: 0.9; }
          100% { transform: translateY(-100vh) scale(0.4); opacity: 0; }
        }
        @keyframes mpfl-fade-in {
          0%   { opacity: 0; transform: translateY(8px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
